import { createApi } from '@reduxjs/toolkit/query/react';
import {
    BaseQueryFn,
    EndpointBuilder,
    FetchArgs,
    FetchBaseQueryError,
    FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query';
import { occupationApiEndpoints } from '@/services/OccupationService.ts';
import { userApiEndpoints } from '@/services/UserService.ts';
import { jobApiEndpoints } from '@/services/JobService.ts';
import { categoryApiEndpoints } from '@/services/CategoryService.ts';
import { assessmentApiEndpoints } from '@/services/AssessmentEngineService';
import { institutionApiEndpoints } from '@/services/InstitutionService';
import { favoritesApiEndpoints } from '@/services/FavoritesService.ts';
import { stateAndEducationApiEndpoints } from '@/services/StateAndEducationService';
import { skillApiEndpoints } from '@/services/SkillService.ts';
import { financialAidApiEndpoints } from '@/services/FinancialAidService.ts';
import { videoApiEndpoints } from '@/services/VideoService.ts';
import { configService } from './ConfigService';
import { sessionService } from './SessionService';
import { featureFlagService } from './FeatureFlagService';
import { userSkillApiEndpoints } from '@/services/UserSkillService.ts';
import { skillCategoryApiEndpoints } from './SkillCategoryService';
import { programApiEndpoints } from '@/services/ProgramService';
import { credentialApiEndpoints } from '@/services/CredentialService';
import { apprenticeshipApiEndpoints } from '@/services/ApprenticeshipService';
import { educationInstitutionApiEndpoints } from '@/services/EducationInstitutionService';
import { featureAccessService } from '@/services/FeatureAccessService';
import { zigbotFeedbackService } from '@/services/ZigBotFeedbackService';
import { userAdminService } from '@/services/UserAdminService';
import { demoAdminService } from '@/services/DemoAdminService';
import { aiApiEndpoints } from '@/services/AiService.ts';
import { userJourneyApiEndpoints } from '@/services/UserJourneyService.ts';
import { skillInferenceApiEndpoints } from '@/services/SkillInferenceService.ts';
import { skillSearchApiEndpoints } from '@/services/SkillSearchService.ts';
import { referralProgramService } from '@/services/ReferralProgramService';
import { challengeApiEndpoints } from '@/services/ChallengeService';
import { shareLinkService } from '@/services/ShareLinkService';
import { cmi5ApiEndpoints } from '@/services/Cmi5Service';
import { earnedBadgeApiEndpoints } from '@/services/EarnedBadgeService';
import { baseQueryWithRefresh, setInvalidateUserTagAction } from '@/lib/apiBaseQuery';

export type BuildType = EndpointBuilder<BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, object, FetchBaseQueryMeta>,
    'User'
    | 'Favorites'
    | 'OccupationsQuery'
    | 'Assessments'
    | 'UserSkills'
    | 'UserCustomSkills'
    | 'UserCustomTasks'
    | 'UserApprenticeships'
    | 'Apprenticeships'
    | 'Programs'
    | 'Credentials'
    | 'UserCredentials'
    | 'UserPrograms'
    | 'Jobs'
    | 'EducationInstitutions'
    | 'FeatureAccess'
    | 'UserAdmin'
    | 'AiFeedback'
    | 'AiConversations'
    | 'UserJourneys'
    | 'SkillInferenceUpload'
    | 'ZigBotFeedback'
    | 'ReferralProgram'
    | 'Cmi5Courses'
    | 'Cmi5Registrations'
    | 'EarnedBadges'
    | 'ShareLinks',
    string>;

const defaultBaseUrl = '/api';

export const setupApi = (baseUrl: string = defaultBaseUrl) => createApi({
    reducerPath: 'api',
    baseQuery: baseQueryWithRefresh(baseUrl),
    tagTypes: ['User', 'Favorites', 'OccupationsQuery', 'Assessments', 'UserSkills', 'UserCustomSkills', 'UserCustomTasks', 'UserApprenticeships', 'Apprenticeships', 'Programs', 'Credentials', 'UserCredentials', 'UserPrograms', 'Jobs', 'EducationInstitutions', 'FeatureAccess', 'UserAdmin', 'AiFeedback', 'AiConversations', 'UserJourneys', 'SkillInferenceUpload', 'ZigBotFeedback', 'ReferralProgram', 'Cmi5Courses', 'Cmi5Registrations', 'EarnedBadges', 'ShareLinks'],
    refetchOnMountOrArgChange: 600, // Cache will invalidate after 600 seconds (10 mins)
    endpoints: (build: BuildType) => ({
        ...userApiEndpoints(build),
        ...occupationApiEndpoints(build),
        ...jobApiEndpoints(build),
        ...programApiEndpoints(build),
        ...credentialApiEndpoints(build),
        ...apprenticeshipApiEndpoints(build),
        ...categoryApiEndpoints(build),
        ...assessmentApiEndpoints(build),
        ...institutionApiEndpoints(build),
        ...favoritesApiEndpoints(build),
        ...stateAndEducationApiEndpoints(build),
        ...skillApiEndpoints(build),
        ...financialAidApiEndpoints(build),
        ...videoApiEndpoints(build),
        ...configService(build),
        ...sessionService(build),
        ...featureFlagService(build),
        ...userSkillApiEndpoints(build),
        ...skillCategoryApiEndpoints(build),
        ...educationInstitutionApiEndpoints(build),
        ...featureAccessService(build),
        ...zigbotFeedbackService(build),
        ...userAdminService(build),
        ...demoAdminService(build),
        ...aiApiEndpoints(build),
        ...userJourneyApiEndpoints(build),
        ...skillInferenceApiEndpoints(build),
        ...cmi5ApiEndpoints(build),
        ...earnedBadgeApiEndpoints(build),
        ...challengeApiEndpoints(build),
        ...skillSearchApiEndpoints(build),
        ...referralProgramService(build),
        ...shareLinkService(build),
    }),
});

export const api = setupApi();

setInvalidateUserTagAction(() => api.util.invalidateTags(['User']));

export const {
    useGetUserQuery,
    useLandingOccupationsQuery,
    useOccupationDetailsQuery,
    useGetMsasByOccupationAndStateQuery,
    useLazyOccupationDetailsQuery,
    useRelatedOccupationsQuery,
    useInstitutionDetailsByOccupationQuery,
    useQueryOccupationsQuery,
    useTrendingOccupationsQuery,
    useNextStepJobsQuery,
    useJobsQuery,
    useJobByExternalSourceValueQuery,
    useProgramDetailsQuery,
    useGetAllCategoriesQuery,
    useGetSubscriptionsQuery,
    useGetAssessmentsByUserQuery,
    useGetAssessmentByIdQuery,
    useGetAssessmentResultQuery,
    useGetRecommendedOccupationsQuery,
    useStartAssessmentMutation,
    useSubmitAssessmentResponseMutation,
    useUpdateAssessmentResponseMutation,
    useScoreAssessmentMutation,
    useInstitutionsQuery,
    useInstitutionDetailsQuery,
    useInstitutionFiltersQuery,
    useGetAllFavoritesQuery,
    useGetRecommendedOccupationsByUserQuery,
    useGetAllStatesAndTerritoriesQuery,
    useGetEducationLevelsQuery,
    useGetUserSkillsQuery,
    useGetUserCustomSkillsQuery,
    useBulkDeleteUserSkillsMutation,
    useQuerySkillsQuery,
    useGetSkillsByExternalValuesQuery,
    useFinancialAidQuery,
    useFinancialAidDetailsQuery,
    useFinancialAidFiltersQuery,
    useGetUserCustomTasksQuery,
    useCreateOrUpdateUserCustomTaskMutation,
    useDeleteUserCustomTaskMutation,
    useGetVideosQuery,
    useSearchStoriesQuery,
    useGetStoryQuery,
    useGetPublicConfigsQuery,
    useGetPartnerLandingBrandingConfigQuery,
    useGetSessionIsExpiredQuery,
    useRefreshSessionMutation,
    useGetFeatureFlagsQuery,
    useGetSkillCategoriesQuery,
    useGetUserSubscriptionResultsQuery,
    useGetRecommendedCategoriesByUserQuery,
    useProgramsQuery,
    useGetProgramTypesQuery,
    useGetCredentialTypesQuery,
    useCredentialsQuery,
    useCredentialDetailsQuery,
    useFavoriteCredentialMutation,
    useGetFavoriteCredentialsQuery,
    useApprenticeshipsQuery,
    useApprenticeshipDetailsQuery,
    useFavoriteApprenticeshipMutation,
    useGetFavoriteApprenticeshipsQuery,
    useUserJobsQuery,
    useEducationInstitutionsQuery,
    useEducationInstitutionDetailsQuery,
    useFavoriteProgramMutation,
    useGetFavoriteProgramsQuery,
    useCheckFeatureAccessQuery,
    useGetFeatureAccessRequestStatusQuery,
    useSubmitFeatureAccessRequestMutation,
    useGrantChatbotAccessMutation,
    useGetPendingFeatureAccessRequestsQuery,
    useApproveFeatureAccessRequestsMutation,
    useRevokeFeatureAccessMutation,
    useGetActiveFeatureAccessUsersQuery,
    useGetDeniedFeatureAccessRequestsQuery,
    useGetFeaturesQuery,
    useDenyFeatureAccessRequestsMutation,
    useGetFeatureAccessSummaryQuery,
    useGetUsersWithoutFeatureAccessQuery,
    useGrantFeatureAccessMutation,
    useSubmitZigBotFeedbackMutation,
    useGetZigBotFeedbackQuery,
    useCheckAdminQuery,
    usePreviewDemoAdminQuery,
    useResetDemoSkillsMutation,
    useClearExtractionJobMutation,
    useAiChatMutation,
    useSubmitAiFeedbackMutation,
    useUpdateAiFeedbackMutation,
    useGetConversationsQuery,
    useGetConversationHistoryQuery,
    useLazyGetConversationHistoryQuery,
    useLazyGetConversationFeedbackQuery,
    useListUserJourneysQuery,
    useGetUserJourneyQuery,
    useCreateUserJourneyMutation,
    useUpdateUserJourneyNameMutation,
    useCompleteUserJourneyMutation,
    useDeleteUserJourneyMutation,
    useCreateUserJourneyTaskMutation,
    useGetJourneyMilestoneRecommendedTasksQuery,
    useGetJourneyRecommendedSkillsQuery,
    useDeleteUserJourneySkillLinkMutation,
    usePatchUserJourneyTaskMutation,
    useDeleteUserJourneyTaskMutation,
    useAddUserJourneySkillLinksMutation,
    useUploadDocumentMutation,
    useGetActiveDocumentUploadQuery,
    useCompleteInferenceJobMutation,
    useRetryInferenceJobMutation,
    useCancelInferenceJobMutation,
    useSearchSkillsQuery,
    useGetReferralProgramStatusQuery,
    useJoinReferralProgramMutation,
    useReferFriendMutation,
    useListCmi5CoursesQuery,
    useGetMyCmi5RegistrationsQuery,
    useLaunchCmi5CourseMutation,
    useFavoriteCmi5CourseMutation,
    useGetEarnedBadgesQuery,
    useGetUnseenEarnedBadgesQuery,
    useGetEarnedBadgeQuery,
    useMarkEarnedBadgesSeenMutation,
    useFavoriteChallengeMutation,
    useMintShareLinkMutation,
    useGetActiveShareLinksQuery,
    useGetShareLinksPageQuery,
    useRevokeShareLinkMutation,
    useRevokeAllShareLinksMutation,
} = api;
